import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import type { LucideIcon } from 'lucide-react';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Brain,
  Check,
  Copy,
  CreditCard,
  Download,
  ExternalLink,
  Image,
  ListOrdered,
  Loader2,
  QrCode,
  RefreshCw,
} from 'lucide-react';
import { useQuiz, useQuizzes, type Quiz } from '../hooks/useQuizzes';
import { api } from '../services/api';

type QuizType = Quiz['quizType'];

/**
 * Single source of truth for the student link.
 *
 * The server encodes the QR from `${env.BASE_URL}/q/:slug`
 * (server/src/routes/quizzes.routes.ts), so the QR image, the download link and the
 * text shown here must all describe the same URL. The browser origin is the only
 * origin the client can know, and it is the origin the admin panel itself is served
 * from — so it must be the same public host as BASE_URL. Building the link in one
 * place keeps the three views consistent; if the panel is ever served from another
 * host, have the server return the public URL instead of relying on this invariant.
 */
function buildPublicUrl(slug: string): string {
  return `${window.location.origin}/q/${slug}`;
}

const quizTypes: { id: QuizType; name: string; icon: LucideIcon }[] = [
  { id: 'memory-game', name: 'Memory Game', icon: Brain },
  { id: 'flashcards', name: 'Flashcards', icon: CreditCard },
  { id: 'image-pairing', name: 'Image Pairing', icon: Image },
  { id: 'image-sequencing', name: 'Image Sequencing', icon: ListOrdered },
];

/** Minimum playable sizes, mirroring the builder wizard. */
const MIN_WORDS: Record<QuizType, number> = {
  'memory-game': 12,
  'image-pairing': 12,
  'image-sequencing': 3,
  flashcards: 1,
};

/**
 * How many words the quiz actually plays with: an explicit selection, or `null`
 * when it falls back to every word of the topic (which the client cannot know).
 */
function knownWordCount(settingsJson: string | null | undefined): number | null {
  if (!settingsJson) return null;
  try {
    const parsed: unknown = JSON.parse(settingsJson);
    if (!parsed || typeof parsed !== 'object') return null;
    const ids: unknown[] = Array.isArray((parsed as Record<string, unknown>).wordIds)
      ? ((parsed as Record<string, unknown>).wordIds as unknown[])
      : [];
    const numbers = ids.filter((id): id is number => typeof id === 'number');
    return numbers.length > 0 ? numbers.length : null;
  } catch {
    return null;
  }
}

function errorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError<{ error?: string }>(err) && err.response?.data?.error) {
    return err.response.data.error;
  }
  return fallback;
}

export default function QuizPublishPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { quiz, loading, fetchQuiz } = useQuiz(Number(id));
  const { updateQuiz } = useQuizzes();
  const [copied, setCopied] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [showQr, setShowQr] = useState(false);
  const [error, setError] = useState('');
  const [pendingType, setPendingType] = useState<QuizType | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);

  if (loading || !quiz) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  const publicUrl = buildPublicUrl(quiz.slug);
  const qrEndpoint = `/api/quizzes/${quiz.id}/qrcode`;
  const wordsInQuiz = knownWordCount(quiz.settingsJson);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setError('');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Не удалось скопировать ссылку. Выделите её в поле и скопируйте вручную.');
    }
  };

  const togglePublish = async () => {
    setError('');
    try {
      await updateQuiz(quiz.id, { isPublished: !quiz.isPublished });
      fetchQuiz();
    } catch (err: unknown) {
      setError(errorMessage(err, 'Не удалось изменить статус публикации'));
    }
  };

  const loadQR = async () => {
    if (qrDataUrl) {
      setShowQr(true);
      return;
    }
    setError('');
    try {
      // Same endpoint as the "Скачать PNG" link below, so preview and file are the
      // exact image the server encodes from BASE_URL.
      const res = await api.get<Blob>(`/quizzes/${quiz.id}/qrcode`, { responseType: 'blob' });
      const reader = new FileReader();
      reader.onloadend = () => {
        setQrDataUrl(reader.result as string);
        setShowQr(true);
      };
      reader.onerror = () => setError('Не удалось прочитать QR-код');
      reader.readAsDataURL(res.data);
    } catch (err: unknown) {
      setError(errorMessage(err, 'Не удалось загрузить QR-код'));
    }
  };

  const confirmTypeChange = async () => {
    if (!pendingType) return;
    setIsSwitching(true);
    setError('');
    try {
      await updateQuiz(quiz.id, { quizType: pendingType });
      await fetchQuiz();
      setPendingType(null);
    } catch (err: unknown) {
      setError(errorMessage(err, 'Не удалось сменить формат теста'));
    } finally {
      setIsSwitching(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(`/topics/${quiz.topicId}`)}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">{quiz.title}</h1>
          <p className="text-sm text-gray-500">Публикация и доступ</p>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Статус</span>
          <button
            onClick={togglePublish}
            aria-label={quiz.isPublished ? 'Снять с публикации' : 'Опубликовать'}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              quiz.isPublished ? 'bg-brand-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                quiz.isPublished ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        <p className="text-sm text-gray-500">
          {quiz.isPublished
            ? 'Тест опубликован и доступен по ссылке.'
            : 'Тест в черновике. Опубликуйте, чтобы поделиться.'}
        </p>
      </div>

      <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
        <h2 className="mb-3 text-lg font-semibold text-gray-900">Формат теста</h2>
        <p className="mb-3 text-sm text-gray-500">
          Тест собирается из слов темы на лету, поэтому формат можно поменять одним кликом — слова и
          ссылка остаются прежними.
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {quizTypes.map((type) => {
            const isCurrent = type.id === quiz.quizType;
            return (
              <button
                key={type.id}
                onClick={() => !isCurrent && setPendingType(type.id)}
                disabled={isCurrent}
                className={`flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                  isCurrent
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200 bg-white hover:border-brand-300 hover:bg-gray-50'
                }`}
              >
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                    isCurrent ? 'bg-brand-100 text-brand-600' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  <type.icon size={18} />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900">{type.name}</span>
                  <span className="block text-xs text-gray-500">
                    {isCurrent ? 'Текущий формат' : 'Переключить на этот'}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
        <h2 className="mb-3 text-lg font-semibold text-gray-900">Публичная ссылка</h2>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            value={publicUrl}
            readOnly
            onFocus={(e) => e.currentTarget.select()}
            className="w-full flex-1 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700"
          />
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
              {copied ? 'Скопировано' : 'Копировать'}
            </button>
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              <ExternalLink size={16} />
              Открыть
            </a>
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-400">
          Правка слов или настроек сразу видна ученикам по этой ссылке. Ссылка и QR-код не меняются.
        </p>
      </div>

      <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
        <h2 className="mb-3 text-lg font-semibold text-gray-900">QR-код</h2>
        {!showQr ? (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={loadQR}
              className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              <QrCode size={16} />
              Показать QR-код
            </button>
            <a
              href={qrEndpoint}
              download={`lexica-${quiz.slug}.png`}
              className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Download size={16} />
              Скачать PNG
            </a>
          </div>
        ) : (
          <div className="space-y-3">
            {qrDataUrl && (
              <img src={qrDataUrl} alt="QR-код теста" className="h-64 w-64 rounded-lg border" />
            )}
            <div className="flex flex-wrap gap-3">
              <a
                href={qrEndpoint}
                download={`lexica-${quiz.slug}.png`}
                className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
              >
                <Download size={16} />
                Скачать PNG
              </a>
              <button
                onClick={() => setShowQr(false)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Скрыть
              </button>
            </div>
            {!quiz.isPublished && (
              <p className="text-xs text-amber-700">
                QR ведёт на страницу, которая откроет ошибку, пока тест не опубликован.
              </p>
            )}
          </div>
        )}
      </div>

      {pendingType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="mt-0.5 shrink-0 text-amber-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Сменить формат теста?</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Формат изменится на «{quizTypes.find((t) => t.id === pendingType)?.name}». Набор слов
                  и ссылка останутся прежними.
                </p>
                {quiz.isPublished && (
                  <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">
                    Внимание: тест опубликован — ученики, которые откроют существующую ссылку или
                    отсканируют QR-код, сразу увидят новый формат.
                  </p>
                )}
                {wordsInQuiz !== null && MIN_WORDS[pendingType] > wordsInQuiz && (
                  <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    Для этого формата нужно минимум {MIN_WORDS[pendingType]} слов, а в тесте их{' '}
                    {wordsInQuiz}. Формат сменится, но играть будет не с чем.
                  </p>
                )}
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={() => setPendingType(null)}
                disabled={isSwitching}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                onClick={confirmTypeChange}
                disabled={isSwitching}
                className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {isSwitching ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                Сменить формат
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}